import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useApi } from '../lib/hooks'
import { ago, money, num, titleCase } from '../lib/format'
import { Delta, Empty, ErrorNote, Loading, Panel, Pill, StatMini } from '../ui/kit'

interface LotRow {
  id: string; code: string; status: string
  farmerName: string; farmerCode: string; cropName: string; gradeCode: string
  farmgateKg: number | null; depotInKg: number | null; deliveredKg: number | null
  pricePerKg: number; purchaseValue: number | null
  totalVariancePct: number | null; unexplainedPct: number | null
  fifoBatch: string | null; expiresAt: string | null; expiringSoon: boolean
  createdAt: string; routeCode: string | null; allocatedKg: number
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = {
  open: 'info',
  in_transit: 'info',
  at_depot: 'warn',
  allocated: 'ok',
  dispatched: 'ok',
  delivered: 'ok',
  closed: 'neutral',
  written_off: 'crit',
  void: 'crit',
}

export function Collections() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const [status, setStatus] = useState('')
  const [unexplainedOnly, setUnexplainedOnly] = useState(false)

  // A farmer can be pinned from the farmer list, so "what did this person bring us"
  // is one click rather than a search.
  const farmerId = params.get('farmerId') ?? ''
  const farmerName = params.get('farmerName') ?? ''

  const query = new URLSearchParams()
  if (status) query.set('status', status)
  if (unexplainedOnly) query.set('unexplainedOnly', 'true')
  if (farmerId) query.set('farmerId', farmerId)
  const qs = query.toString()

  const lots = useApi<LotRow[]>(`/collections/lots${qs ? `?${qs}` : ''}`, { deps: [farmerId] })

  const openVariance = lots.data?.filter((l) => (l.unexplainedPct ?? 0) > 0) ?? []
  const expiring = lots.data?.filter((l) => l.expiringSoon && l.status !== 'closed') ?? []
  const totalValue = lots.data?.reduce((sum, l) => sum + (l.purchaseValue ?? 0), 0) ?? 0

  return (
    <main className="page">
      <div className="page-head">
        <h1>Collections</h1>
        <p>
          Every collection is what one farmer brought you, of one crop, on one day. It stays open
          until every kilo is accounted for.
        </p>
      </div>

      {farmerId && (
        <div className="filter-pin">
          <span>
            Showing only <b>{farmerName || 'one farmer'}</b>
          </span>
          <button className="btn quiet" style={{ minHeight: 26, fontSize: 'var(--fs-xs)' }}
            onClick={() => setParams({})}>
            Show everyone
          </button>
        </div>
      )}

      <Panel
        title="All collections"
        meta={
          <div className="row" style={{ gap: 8 }}>
            <select className="input" style={{ minHeight: 30, width: 'auto' }}
              value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="open">Open — in the field</option>
              <option value="at_depot">At depot</option>
              <option value="allocated">Allocated</option>
              <option value="delivered">Delivered</option>
              <option value="closed">Closed</option>
            </select>
            <button
              className={`btn ${unexplainedOnly ? '' : 'quiet'}`}
              style={{ minHeight: 30, padding: '4px 12px', fontSize: 'var(--fs-xs)' }}
              onClick={() => setUnexplainedOnly((v) => !v)}
            >
              Unexplained only
            </button>
          </div>
        }
        footer={
          <>
            <StatMini label="Collections shown" value={lots.data?.length ?? 0} />
            <StatMini label="Cost to you" value={money(totalValue, { compact: true })} />
            <StatMini label="Waiting for a reason" value={openVariance.length}
              note={openVariance.length > 0 ? 'cannot close' : 'all reconciled'} />
            <StatMini label="Goes off within 12 h" value={expiring.length}
              note={expiring.length > 0 ? 'sell today or write off' : 'none'} />
          </>
        }
      >
        {lots.error && <ErrorNote error={lots.error} onRetry={lots.reload} />}
        {lots.loading && <Loading rows={6} />}
        {lots.data?.length === 0 && (
          <Empty title="Nothing matches">
            Change the filter, or capture a collection from the field app to create one.
          </Empty>
        )}
        {lots.data && lots.data.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Collection</th>
                  <th>Farmer</th>
                  <th>Crop</th>
                  <th className="n">At the farm</th>
                  <th className="n">At the store</th>
                  <th className="n">Difference</th>
                  <th className="n">Value</th>
                  <th>Status</th>
                  <th className="n">Captured</th>
                </tr>
              </thead>
              <tbody>
                {lots.data.map((l) => {
                  const unexplained = (l.unexplainedPct ?? 0) > 0
                  return (
                    <tr
                      key={l.id}
                      className={`clickable ${unexplained ? 'attention' : ''}`}
                      onClick={() => nav(`/console/collections/${l.code}`)}
                    >
                      <td className="strong code">
                        {/* A real link as well as the row click: the row alone cannot be
                            reached by keyboard and gives no address to copy or open in a tab. */}
                        <Link to={`/console/collections/${l.code}`} className="row-link"
                          onClick={(e) => e.stopPropagation()}>
                          {l.code}
                        </Link>
                      </td>
                      <td className="truncate">{l.farmerName}</td>
                      <td>
                        {l.cropName} <span className="faint">{l.gradeCode}</span>
                        {l.expiringSoon && l.status !== 'closed' && (
                          <> <Pill tone="warn" dot={false}>expiring</Pill></>
                        )}
                      </td>
                      <td className="n">{num(l.farmgateKg, 1)}</td>
                      <td className="n">{l.depotInKg === null ? '—' : num(l.depotInKg, 1)}</td>
                      <td className="n">
                        <Delta value={l.totalVariancePct} goodDirection="zero" decimals={2} />
                        {unexplained && <> <Pill tone="crit" dot={false}>open</Pill></>}
                      </td>
                      <td className="n">{money(l.purchaseValue, { currency: false })}</td>
                      <td><Pill tone={STATUS_TONE[l.status] ?? 'neutral'}>{titleCase(l.status)}</Pill></td>
                      <td className="n faint">{ago(l.createdAt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </main>
  )
}
